import { FeatureFlagKey } from "@redotech/feature-flags-model/keys";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import SettingsIcon from "@redotech/redo-icons/settings-02.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  UserContext,
  UserContextValue,
} from "@redotech/redo-merchant-app-common/user";
import {
  DropoffProviderType,
  dropoffProviderType,
  getMerchantFacingProviderName,
} from "@redotech/redo-model/dropoff/dropoff-provider";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import { FlowType, Payer, Step } from "@redotech/redo-model/return-flow";
import { isExchangeAgentEnabledForTeam } from "@redotech/redo-model/return-flow/exchange-agent/exchange-agent-utils";
import {
  ReturnOptionMethod,
  StoreCreditType,
} from "@redotech/redo-model/return-flow/return-option";
import { AdvancedExchanges, Team } from "@redotech/redo-model/team";
import { ReturnState } from "@redotech/redo-return-ui/flows/return-flow/return";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { ButtonSize, IconButton } from "@redotech/redo-web/button";
import { CollapseSubsection } from "@redotech/redo-web/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@redotech/redo-web/components/ui/dropdown-menu";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { InfoTooltip } from "@redotech/redo-web/info-tooltip";
import {
  ProductAutocomplete,
  ProductVariantInfo,
} from "@redotech/redo-web/product-autocomplete";
import { Text } from "@redotech/redo-web/text";
import { Tooltip } from "@redotech/redo-web/tooltip/tooltip";
import { produce } from "immer";
import { memo, useContext, useMemo, useState } from "react";
import { ExchangeAgentConfiguration } from "./exchange-agent-configuration";
import { useProductAutocomplete } from "./hooks/use-product-autocomplete";
import { PerCurrencyOverrides } from "./per-currency-overrides";
import { RequiredMessage } from "./required-message";
import * as returnCss from "./return.module.css";
import { isBonusAdjustment } from "./util";

export const returnOptionMethods: ReturnOptionMethod["type"][] = [
  ReturnOptionMethod.EXCHANGE,
  ReturnOptionMethod.STORE_CREDIT,
  ReturnOptionMethod.REFUND,
];

function returnOptionMethod(
  value: ReturnOptionMethod["type"] | undefined,
): string | undefined {
  switch (value) {
    case ReturnOptionMethod.STORE_CREDIT:
      return "Store credit";
    case ReturnOptionMethod.EXCHANGE:
      return "Exchange";
    case ReturnOptionMethod.REFUND:
      return "Refund";
  }
  return undefined;
}

type AdjustmentType = "flat" | "percent";
type AdjustmentCategory = "bonus" | "fee";

interface AdjustmentCategoryData {
  category: AdjustmentCategory;
  label: string;
}

const adjustmentCategoryOptions: RedoListItem<AdjustmentCategoryData>[] = [
  {
    type: "text",
    id: "bonus",
    text: "Bonus",
    value: { category: "bonus", label: "Bonus" },
  },
  {
    type: "text",
    id: "fee",
    text: "Fee",
    value: { category: "fee", label: "Fee" },
  },
];

export const AdjustmentElement = memo(function AdjustmentElement({
  adjustmentFlat,
  adjustmentPercent,
  handleAdjustmentFlat,
  handleAdjustmentPercent,
  canEditReturnsSettings,
  currencyOverrides,
  handleCurrencyOverridesChange,
  perCurrencyAmountsEnabled,
  handlePerCurrencyAmountsEnabledChange,
}: {
  adjustmentFlat: string;
  adjustmentPercent: string;
  handleAdjustmentFlat(value: string): void;
  handleAdjustmentPercent(value: string): void;
  canEditReturnsSettings: boolean;
  currencyOverrides?: Record<string, string>;
  handleCurrencyOverridesChange?: (overrides: Record<string, string>) => void;
  perCurrencyAmountsEnabled?: boolean;
  handlePerCurrencyAmountsEnabledChange?: (enabled: boolean) => void;
}) {
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>(() => {
    if (adjustmentPercent !== "0") {
      return "percent";
    }
    return "flat";
  });

  const currentValue =
    adjustmentType === "flat" ? adjustmentFlat : adjustmentPercent;
  const isNegative = parseFloat(currentValue) < 0;
  const adjustmentCategory: AdjustmentCategory = isNegative ? "fee" : "bonus";

  const formatAdjustmentFlat = useHandler((value: string) => {
    handleAdjustmentFlat((+value).toFixed(2));
  });
  const formatAdjustmentPercent = useHandler((value: string) => {
    if (isNaN(parseFloat(value))) {
      handleAdjustmentPercent("0");
    }
  });

  const handleValueChange =
    adjustmentType === "flat" ? handleAdjustmentFlat : handleAdjustmentPercent;
  const formatValue =
    adjustmentType === "flat" ? formatAdjustmentFlat : formatAdjustmentPercent;

  const selectedCategory =
    adjustmentCategoryOptions.find(
      (opt) => opt.value.category === adjustmentCategory,
    ) || adjustmentCategoryOptions[0];

  const handleCategoryChange = useHandler((category: AdjustmentCategory) => {
    const numValue = parseFloat(currentValue);
    if (isNaN(numValue)) return;

    const absValue = Math.abs(numValue);
    const newValue = category === "fee" ? -absValue : absValue;

    if (adjustmentType === "flat") {
      handleAdjustmentFlat(newValue.toFixed(2));
    } else {
      handleAdjustmentPercent(String(newValue));
    }
  });

  const displayValue = Math.abs(parseFloat(currentValue) || 0).toString();

  const handleDisplayValueChange = useHandler((value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      handleValueChange("0");
      return;
    }

    const signedValue =
      adjustmentCategory === "fee" ? -Math.abs(numValue) : Math.abs(numValue);

    if (adjustmentType === "flat") {
      handleAdjustmentFlat(signedValue.toFixed(2));
    } else {
      handleAdjustmentPercent(String(signedValue));
    }
  });

  const currencySymbol = adjustmentType === "flat" ? "$" : "%";

  return (
    <Flex dir="column" gap="md">
      <RedoSingleSelectDropdownInput
        disabled={!canEditReturnsSettings}
        label="Adjustment type"
        options={adjustmentCategoryOptions}
        optionSelected={({ item }) => handleCategoryChange(item.value.category)}
        selectedItem={selectedCategory}
        size="xs"
      />
      <RedoTextInput
        errorText={
          isNaN(parseFloat(displayValue)) ? "Invalid number" : undefined
        }
        fullWidth
        inputTypeHint="number"
        label="Adjustment amount"
        onBlur={() => formatValue(currentValue)}
        setValue={handleDisplayValueChange}
        state={canEditReturnsSettings ? "default" : "readonly"}
        trailingActions={
          canEditReturnsSettings ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <RedoButton
                  hierarchy="tertiary"
                  IconTrailing={ChevronDownIcon}
                  size="xs"
                  text={currencySymbol}
                  type="button"
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" style={{ zIndex: 1000 }}>
                <DropdownMenuItem
                  onClick={(e) => {
                    setAdjustmentType("flat");
                    handleAdjustmentFlat(currentValue);
                  }}
                >
                  $ Fixed dollar amount
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    setAdjustmentType("percent");
                    handleAdjustmentPercent(currentValue);
                  }}
                >
                  % Percentage
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Text fontSize="sm" p="xs" textColor="secondary">
              {currencySymbol}
            </Text>
          )
        }
        value={displayValue}
      />
      {adjustmentType === "flat" &&
        handleCurrencyOverridesChange &&
        handlePerCurrencyAmountsEnabledChange && (
          <PerCurrencyOverrides
            baseAmount={adjustmentFlat}
            currencyOverrides={currencyOverrides ?? {}}
            disabled={!canEditReturnsSettings}
            onCurrencyOverridesChange={handleCurrencyOverridesChange}
            onPerCurrencyAmountsEnabledChange={
              handlePerCurrencyAmountsEnabledChange
            }
            perCurrencyAmountsEnabled={perCurrencyAmountsEnabled ?? false}
          />
        )}
    </Flex>
  );
});

export const ReturnDetails = memo(function ReturnDetails({
  state,
  setState,
  allSteps,
  step,
  setStateWithId,
  flowType,
}: StepTypeDetailsProps<ReturnState>) {
  const [
    showInternalOnlyExchangeAgentConfiguration,
    setShowInternalOnlyExchangeAgentConfiguration,
  ] = useState(false);

  const [selectedDropoffProvider, setSelectedDropoffProvider] =
    useState<DropoffProviderType>(dropoffProviderType[0]);

  const user = useContext(UserContext);
  const team = useContext(TeamContext);
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );
  const racFlagLoad = useLoad(async () => {
    const response = await platformRpcClient.getFeatureFlag({
      flagKey: FeatureFlagKey.RETURN_ACCOUNTING_CALCULATOR,
    });
    return response.enabled;
  }, [platformRpcClient]);

  const exchangeAgentConfig = useExchangeAgentOverride({
    team,
    user,
    flowType,
  });

  const handleFlatShipping = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.flatShipping = value;
        // Flat shipping and label upcharge are mutually exclusive
        if (value) {
          state.labelUpcharge = false;
        }
      }),
    );
  });

  const handleFlatShippingRate = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.flatShippingRate = value;
      }),
    );
  });

  const handleFlatShippingCurrencyOverrides = useHandler(
    (overrides: Record<string, string>) => {
      setState((state) =>
        produce(state, (state) => {
          state.flatShippingCurrencyOverrides = Object.fromEntries(
            Object.entries(overrides)
              .filter(([, v]) => v !== undefined && !isNaN(+v))
              .map(([k, v]) => [k, +v]),
          );
        }),
      );
    },
  );

  const handleFlatShippingPerCurrencyAmountsEnabled = useHandler(
    (enabled: boolean) => {
      setState((state) =>
        produce(state, (state) => {
          state.flatShippingPerCurrencyAmountsEnabled = enabled;
        }),
      );
    },
  );

  const handleMerchantLabelAdjustment = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.labelUpcharge = value;
        // Flat shipping and label upcharge are mutually exclusive
        if (value) {
          state.flatShipping = false;
        }
      }),
    );
  });

  const handleLabelUpchargeType = useHandler(
    (value: Step.LabelUpchargeType) => {
      setState((state) =>
        produce(state, (state) => {
          state.labelUpchargeType = value;
        }),
      );
    },
  );

  const handleLabelUpchargeValue = useHandler((value: string) => {
    setState((state) =>
      produce(state, (state) => {
        state.labelUpchargeValue = value;
      }),
    );
  });

  const handleDropoffFeeChange = useHandler(
    (provider: (typeof dropoffProviderType)[number], fee: string) => {
      setState((state) =>
        produce(state, (state) => {
          if (!state.dropoffFees) {
            state.dropoffFees = [];
          }
          const entry = state.dropoffFees.find((f) => f.provider === provider);
          if (entry) {
            entry.fee = fee;
          } else {
            state.dropoffFees.push({ provider, fee });
          }
        }),
      );
    },
  );

  const handleDropoffFeeCurrencyOverridesChange = useHandler(
    (
      provider: (typeof dropoffProviderType)[number],
      overrides: Record<string, string>,
    ) => {
      const asNumbers = Object.fromEntries(
        Object.entries(overrides)
          .filter(([, v]) => v !== undefined && !isNaN(+v))
          .map(([k, v]) => [k, +v]),
      );
      setState((state) =>
        produce(state, (state) => {
          if (!state.dropoffFees) {
            state.dropoffFees = [];
          }
          const entry = state.dropoffFees.find((f) => f.provider === provider);
          if (entry) {
            entry.currencyOverrides = asNumbers;
          } else {
            state.dropoffFees.push({
              provider,
              fee: "0.00",
              currencyOverrides: asNumbers,
            });
          }
        }),
      );
    },
  );

  const handleDropoffFeePerCurrencyAmountsEnabledChange = useHandler(
    (
      provider: (typeof dropoffProviderType)[number],
      perCurrencyAmountsEnabled: boolean,
    ) => {
      setState((state) =>
        produce(state, (state) => {
          if (!state.dropoffFees) {
            state.dropoffFees = [];
          }
          const entry = state.dropoffFees.find((f) => f.provider === provider);
          if (entry) {
            entry.perCurrencyAmountsEnabled = perCurrencyAmountsEnabled;
          } else {
            state.dropoffFees.push({
              provider,
              fee: "0.00",
              perCurrencyAmountsEnabled,
            });
          }
        }),
      );
    },
  );

  const handleCoverPackagePickup = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.coverPackagePickup = value;
      }),
    );
  });

  const handleExchangeAgentDisable = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.exchangeAgentOverrides = {
          ...state.exchangeAgentOverrides,
          disableExchangeAgent: value,
        };
      }),
    );
  });

  const handleVariantRecommendationsDisable = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.exchangeAgentOverrides = {
          ...state.exchangeAgentOverrides,
          disableVariantRecommendations: value,
        };
      }),
    );
  });

  const handleProductRecommendationsDisable = useHandler((value: boolean) => {
    setState((state) =>
      produce(state, (state) => {
        state.exchangeAgentOverrides = {
          ...state.exchangeAgentOverrides,
          disableProductRecommendations: value,
        };
      }),
    );
  });

  // Contexts required to be defined
  if (!team) {
    return null;
  }

  const getOptionForType = (type: ReturnOptionMethod["type"]) => {
    return state.options.find((opt) => opt.type === type);
  };

  const isOptionEnabled = (type: ReturnOptionMethod["type"]) => {
    return !!getOptionForType(type);
  };

  const allReturnTypes: ReturnOptionMethod["type"][] = [
    ReturnOptionMethod.EXCHANGE,
    ReturnOptionMethod.STORE_CREDIT,
    ReturnOptionMethod.REFUND,
  ];

  const hasAtLeastOneEnabled = allReturnTypes.some((type) =>
    isOptionEnabled(type),
  );

  return (
    <>
      {showInternalOnlyExchangeAgentConfiguration && (
        <RedoModal
          isOpen={showInternalOnlyExchangeAgentConfiguration}
          modalSize="lg"
          onModalCloseRequested={() =>
            setShowInternalOnlyExchangeAgentConfiguration(false)
          }
          title="Internal Only Exchange Agent Configuration"
        >
          <ExchangeAgentConfiguration
            allSteps={allSteps}
            canEditSettings={canEditReturnsSettings}
            setStateWithId={setStateWithId}
            step={step}
          />
        </RedoModal>
      )}
      <Flex dir="column" gap="xl">
        {allReturnTypes.map((type, index) => (
          <StateOptionComponent
            canEditReturnsSettings={canEditReturnsSettings}
            isEnabled={isOptionEnabled(type)}
            key={index}
            option={getOptionForType(type)}
            optionType={type}
            racEnabled={!!racFlagLoad.value}
            setState={setState}
            state={state}
            team={team}
          />
        ))}
      </Flex>
      {!hasAtLeastOneEnabled && (
        <RequiredMessage>Must have at least one option enabled</RequiredMessage>
      )}
      <Divider />
      <RedoCollapseSubsection title="Advanced return options">
        <Flex dir="column" gap="sm" mb="md" pl="4xl">
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            Drop-off fees
          </Text>
          <Flex dir="column" gap="md">
            <Text fontSize="xxs" textColor="secondary">
              Flat fee charged to customers per drop-off provider
            </Text>
            <RedoSingleSelectDropdownInput
              disabled={!canEditReturnsSettings}
              label="Provider"
              options={dropoffProviderType.map((provider) => ({
                type: "text" as const,
                id: provider,
                text: getMerchantFacingProviderName(provider),
                value: provider,
              }))}
              optionSelected={({ item }) =>
                setSelectedDropoffProvider(item.value)
              }
              selectedItem={{
                type: "text" as const,
                id: selectedDropoffProvider,
                text: getMerchantFacingProviderName(selectedDropoffProvider),
                value: selectedDropoffProvider,
              }}
            />
            <RedoTextInput
              errorText={(() => {
                const num = parseFloat(
                  state.dropoffFees?.find(
                    (f) => f.provider === selectedDropoffProvider,
                  )?.fee ?? "0",
                );
                if (isNaN(num)) return "Invalid amount";
                if (num < 0) return "Fee must be a positive amount";
                return undefined;
              })()}
              fullWidth
              inputTypeHint="number"
              label="Fee"
              onBlur={() => {
                const raw =
                  state.dropoffFees?.find(
                    (f) => f.provider === selectedDropoffProvider,
                  )?.fee ?? "0";
                const num = parseFloat(raw);
                handleDropoffFeeChange(
                  selectedDropoffProvider,
                  isNaN(num) || num < 0 ? "0.00" : num.toFixed(2),
                );
              }}
              setValue={(value) =>
                handleDropoffFeeChange(selectedDropoffProvider, value)
              }
              state={canEditReturnsSettings ? "default" : "readonly"}
              trailingActions={
                <Text fontSize="sm" p="xs" textColor="secondary">
                  $
                </Text>
              }
              value={
                state.dropoffFees?.find(
                  (f) => f.provider === selectedDropoffProvider,
                )?.fee ?? "0"
              }
            />
            <PerCurrencyOverrides
              baseAmount={
                state.dropoffFees?.find(
                  (f) => f.provider === selectedDropoffProvider,
                )?.fee ?? "0"
              }
              currencyOverrides={Object.fromEntries(
                Object.entries(
                  state.dropoffFees?.find(
                    (f) => f.provider === selectedDropoffProvider,
                  )?.currencyOverrides ?? {},
                ).map(([k, v]) => [k, String(v)]),
              )}
              disabled={!canEditReturnsSettings}
              onCurrencyOverridesChange={(overrides) =>
                handleDropoffFeeCurrencyOverridesChange(
                  selectedDropoffProvider,
                  overrides,
                )
              }
              onPerCurrencyAmountsEnabledChange={(enabled) =>
                handleDropoffFeePerCurrencyAmountsEnabledChange(
                  selectedDropoffProvider,
                  enabled,
                )
              }
              perCurrencyAmountsEnabled={
                state.dropoffFees?.find(
                  (f) => f.provider === selectedDropoffProvider,
                )?.perCurrencyAmountsEnabled ?? false
              }
            />
          </Flex>
        </Flex>
        <Flex dir="column" gap="sm" mb="md">
          <RedoToggle
            description="Charge customers a flat cost for return shipping labels. Merchant will receive/pay for the difference between actual cost."
            disabled={!canEditReturnsSettings || state.labelUpcharge}
            label="Flat shipping cost"
            setValue={handleFlatShipping}
            size="xs"
            value={state.flatShipping || false}
          />
          {state.flatShipping && (
            <Flex dir="column" gap="md" pl="4xl">
              <RedoTextInput
                IconLeading={() => <Text fontSize="xs">$</Text>}
                inputTypeHint="number"
                placeholder="0.00"
                setValue={handleFlatShippingRate}
                state={canEditReturnsSettings ? "default" : "readonly"}
                value={state.flatShippingRate || ""}
              />
              <PerCurrencyOverrides
                baseAmount={state.flatShippingRate || "0"}
                currencyOverrides={Object.fromEntries(
                  Object.entries(state.flatShippingCurrencyOverrides ?? {}).map(
                    ([k, v]) => [k, String(v)],
                  ),
                )}
                disabled={!canEditReturnsSettings}
                onCurrencyOverridesChange={handleFlatShippingCurrencyOverrides}
                onPerCurrencyAmountsEnabledChange={
                  handleFlatShippingPerCurrencyAmountsEnabled
                }
                perCurrencyAmountsEnabled={
                  state.flatShippingPerCurrencyAmountsEnabled ?? false
                }
                tooltipText="Specify exact flat shipping costs for specific currencies. Currencies not listed will use the default rate above."
              />
            </Flex>
          )}
        </Flex>
        <RedoToggle
          description="Adjust the shipping label cost by a fixed amount or percentage. Positive values increase the cost (upcharge), negative values decrease it (discount)."
          disabled={!canEditReturnsSettings || state.flatShipping}
          label="Label cost adjustment"
          setValue={handleMerchantLabelAdjustment}
          size="xs"
          value={state.labelUpcharge || false}
        />
        {state.labelUpcharge && (
          <Flex dir="column" gap="md" pl="4xl">
            <RedoTextInput
              fullWidth
              inputTypeHint="number"
              setValue={handleLabelUpchargeValue}
              state={canEditReturnsSettings ? "default" : "readonly"}
              trailingActions={
                canEditReturnsSettings ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <RedoButton
                        hierarchy="tertiary"
                        IconTrailing={ChevronDownIcon}
                        size="xs"
                        text={
                          state.labelUpchargeType ===
                          Step.LabelUpchargeType.PERCENTAGE
                            ? "%"
                            : "$"
                        }
                        type="button"
                      />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" style={{ zIndex: 1000 }}>
                      <DropdownMenuItem
                        onClick={() =>
                          handleLabelUpchargeType(Step.LabelUpchargeType.FIXED)
                        }
                      >
                        $ Fixed dollar amount
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          handleLabelUpchargeType(
                            Step.LabelUpchargeType.PERCENTAGE,
                          )
                        }
                      >
                        % Percentage
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Text fontSize="sm" p="xs" textColor="secondary">
                    {state.labelUpchargeType ===
                    Step.LabelUpchargeType.PERCENTAGE
                      ? "%"
                      : "$"}
                  </Text>
                )
              }
              value={state.labelUpchargeValue || "0"}
            />
          </Flex>
        )}
        {team.settings.pickupEnabled && (
          <RedoToggle
            description={`Merchant covers package pickup costs up to the max amount set in the shipping settings ${team.settings.pickupMaxCoveredAmountEnabled ? `($${team.settings.pickupMaxCoveredAmount?.toFixed(2) ?? "0.00"})` : "(no max set)"}`}
            disabled={!canEditReturnsSettings}
            label="Cover package pickup"
            setValue={handleCoverPackagePickup}
            size="xs"
            value={state.coverPackagePickup || false}
          />
        )}
        {exchangeAgentConfig.exchangeAgentEnabled && (
          <Flex dir="column">
            <Divider />
            <RedoExternalLink
              fontSize="md"
              text="Exchange Agent settings"
              url={exchangeAgentConfig.settingsLink}
            />
            <Flex align="center" dir="row" justify="space-between">
              <RedoToggle
                description="Don't use Exchange Agent for this step"
                disabled={!canEditReturnsSettings}
                label="Disable Exchange Agent"
                setValue={handleExchangeAgentDisable}
                size="xs"
                value={
                  state.exchangeAgentOverrides?.disableExchangeAgent ?? false
                }
              />
              <Flex dir="column" gap="xxs">
                <Flex align="center" dir="row" gap="sm">
                  {exchangeAgentConfig.displayInternalOnly && (
                    <Tooltip title="Internal only Exchange Agent settings">
                      <IconButton
                        disabled={!canEditReturnsSettings}
                        onClick={() =>
                          setShowInternalOnlyExchangeAgentConfiguration(true)
                        }
                        size={ButtonSize.SMALL}
                      >
                        <SettingsIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Flex>
              </Flex>
            </Flex>
            {!state.exchangeAgentOverrides?.disableExchangeAgent && (
              <Flex dir="column">
                <RedoToggle
                  description="Don't recommend variant exchanges for this step"
                  disabled={
                    !canEditReturnsSettings ||
                    !exchangeAgentConfig.variantRecommendationsEnabled
                  }
                  label="Disable variant recommendations"
                  setValue={handleVariantRecommendationsDisable}
                  size="xs"
                  value={
                    state.exchangeAgentOverrides
                      ?.disableVariantRecommendations ?? false
                  }
                />
                {exchangeAgentConfig.productRecommendationsEnabled && (
                  <RedoToggle
                    description="Don't recommend product exchanges for this step"
                    disabled={
                      !canEditReturnsSettings ||
                      !exchangeAgentConfig.productRecommendationsEnabled
                    }
                    label="Disable product recommendations"
                    setValue={handleProductRecommendationsDisable}
                    size="xs"
                    value={
                      state.exchangeAgentOverrides
                        ?.disableProductRecommendations ?? false
                    }
                  />
                )}
                {(!exchangeAgentConfig.variantRecommendationsEnabled ||
                  !exchangeAgentConfig.productRecommendationsEnabled) && (
                  <Flex dir="column" pt="md">
                    {!exchangeAgentConfig.variantRecommendationsEnabled && (
                      <Text fontSize="sm" textOverflow="wrap">
                        Variant recommendations are currently disabled
                      </Text>
                    )}
                    {!exchangeAgentConfig.productRecommendationsEnabled && (
                      <Text fontSize="sm" textOverflow="wrap">
                        Product recommendations are currently disabled
                      </Text>
                    )}
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>
        )}
      </RedoCollapseSubsection>
    </>
  );
});

const useExchangeAgentOverride = ({
  team,
  user,
  flowType,
}: {
  team: Team | undefined;
  user: UserContextValue | undefined;
  flowType: FlowType;
}): {
  exchangeAgentEnabled: boolean;
  displayInternalOnly?: boolean;
  variantRecommendationsEnabled?: boolean;
  productRecommendationsEnabled?: boolean;
  settingsLink?: string;
} => {
  const {
    exchangeAgentEnabled,
    variantRecommendationsEnabled,
    productRecommendationsEnabled,
  } = useMemo(() => {
    if (!team || flowType !== FlowType.RETURN) {
      return {
        exchangeAgentEnabled: false,
        variantRecommendationsEnabled: false,
        productRecommendationsEnabled: false,
      };
    }
    const exchangeAgentEnabled = isExchangeAgentEnabledForTeam(
      team.settings.returnsAiExchange,
      team.settings.exchangeAgent,
      team,
    );
    const variantRecommendationsEnabled =
      team.settings.exchangeAgent.variantRecommendations.enabled;
    const productRecommendationsEnabled =
      team.settings.exchangeAgent.productRecommendations.enabled;
    return {
      exchangeAgentEnabled,
      variantRecommendationsEnabled,
      productRecommendationsEnabled,
    };
  }, [team, flowType]);

  if (!exchangeAgentEnabled) {
    return { exchangeAgentEnabled };
  }

  return {
    exchangeAgentEnabled,
    displayInternalOnly: !!(user?.email && /@getredo\.com/.test(user?.email)),
    variantRecommendationsEnabled,
    productRecommendationsEnabled,
    settingsLink: `${window.location.href.split("/settings")[0]}/settings/exchanges`,
  };
};

const StateOptionComponent = memo(function StateOptionComponent({
  team,
  state,
  setState,
  canEditReturnsSettings,
  optionType,
  option,
  isEnabled,
  racEnabled,
}: {
  team: Team;
  state: StepTypeDetailsProps<ReturnState>["state"];
  setState: StepTypeDetailsProps<ReturnState>["setState"];
  canEditReturnsSettings: boolean;
  optionType: ReturnOptionMethod["type"];
  option: ReturnState["options"][number] | undefined;
  isEnabled: boolean;
  racEnabled: boolean;
}) {
  const { productsState, selectedProduct, onSelectedProductChange } =
    useProductAutocomplete(team._id, option?.variantExchangeAddOnVariantId);

  const getIndex = () =>
    state.options.findIndex((opt) => opt.type === optionType);

  const handleToggle = useHandler((enabled: boolean) => {
    setState((state) =>
      produce(state, (draft) => {
        if (enabled) {
          draft.options.push({
            type: optionType,
            creditType: StoreCreditType.GIFT_CARD,
            labelPayer: Payer.CUSTOMER,
            ship: true,
            manual_label: false,
            exchangeTypes: {
              sameItem: true,
              variant: true,
              exchangeGroups: true,
              advanced: true,
            },
            chargeCustomerForNewOrderShipping: false,
            adjustmentFlat: "0",
            adjustmentPercent: "0",
            adjustmentType: "flat",
            refundPointsRedeemedForItem: false,
            deductEarnedPointsFromRefund: false,
            reimburseForFinalSale: true,
          });
        } else {
          const index = draft.options.findIndex(
            (opt) => opt.type === optionType,
          );
          if (index !== -1) {
            draft.options.splice(index, 1);
          }
        }
      }),
    );
  });

  const handleAdjustmentFlat = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].adjustmentFlat = value;
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].adjustmentPercent = "0";
        if (parseFloat(value) < 0) {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.options[index].isUseItOrLoseIt = false;
        }
      }),
    );
  });

  const handleAdjustmentPercent = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].adjustmentPercent = value;
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].adjustmentFlat = "0";
        if (parseFloat(value) < 0) {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.options[index].isUseItOrLoseIt = false;
        }
      }),
    );
  });

  const handleCurrencyOverrides = useHandler(
    (overrides: Record<string, string>) => {
      const index = getIndex();
      if (index === -1) return;
      setState((state) =>
        produce(state, (state) => {
          const option = state.options[index];
          if (option) {
            option.currencyOverrides = overrides;
          }
        }),
      );
    },
  );

  const handlePerCurrencyAmountsEnabled = useHandler((enabled: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        const option = state.options[index];
        if (option) {
          option.perCurrencyAmountsEnabled = enabled;
        }
      }),
    );
  });

  const handleGreenReturn = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].ship = !value;
      }),
    );
  });

  const handleManualLabel = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].manual_label = value;
        if (value) {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.options[index].ship = true;
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.options[index].labelPayer = Payer.CUSTOMER;
        }
      }),
    );
  });

  const handleSafetyNet = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].labelPayer = value
          ? Payer.MERCHANT
          : Payer.CUSTOMER;
      }),
    );
  });

  const handleExchangeTypeSameItem = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].exchangeTypes.sameItem = value;
      }),
    );
  });

  const handleExchangeTypeVariant = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].exchangeTypes.variant = value;
      }),
    );
  });

  const handleExchangeTypeExchangeGroups = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].exchangeTypes.exchangeGroups = value;
      }),
    );
  });

  const handleExchangeTypeAdvanced = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].exchangeTypes.advanced = value;
        if (!value) {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          state.options[index].isUseItOrLoseIt = false;
        }
      }),
    );
  });

  const handleVariantExchangeVariantId = useHandler(
    (value: ProductVariantInfo | null | undefined) => {
      const index = getIndex();
      if (index === -1) return;
      onSelectedProductChange(value ?? null);
      if (value) {
        setState((state) =>
          produce(state, (state) => {
            // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
            state.options[index].variantExchangeAddOnVariantId =
              value.variantId;
          }),
        );
      }
    },
  );

  const handleVariantExchangeAddOnTitle = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].variantExchangeAddOnTitle = value;
      }),
    );
  });

  const handleVariantExchangeAddOnAcceptButton = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].variantExchangeAddOnAcceptButton = value;
      }),
    );
  });

  const handleVariantExchangeAddOnRejectButton = useHandler((value: string) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].variantExchangeAddOnRejectButton = value;
      }),
    );
  });

  const handleExchangeShipping = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].chargeCustomerForNewOrderShipping = value;
      }),
    );
  });

  const handleRefundPointsRedeemed = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].refundPointsRedeemedForItem = value;
      }),
    );
  });

  const handleDeductEarnedPoints = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].deductEarnedPointsFromRefund = value;
      }),
    );
  });

  const handleReimburseForFinalSale = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].reimburseForFinalSale = value;
      }),
    );
  });

  const handleIsUseItOrLoseIt = useHandler((value: boolean) => {
    const index = getIndex();
    if (index === -1) return;
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.options[index].isUseItOrLoseIt = value;
      }),
    );
  });
  if (!isEnabled) {
    return (
      <Flex className={returnCss.optionCard} dir="column" gap="md">
        <Flex align="center" dir="row" justify="space-between">
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {returnOptionMethod(optionType)}
          </Text>
          <RedoToggle
            disabled={!canEditReturnsSettings}
            setValue={handleToggle}
            size="xs"
            value={false}
          />
        </Flex>
      </Flex>
    );
  }

  const isBonus =
    isBonusAdjustment(option?.adjustmentFlat || "0") &&
    isBonusAdjustment(option?.adjustmentPercent || "0");
  const optionHasAdvancedExchanges =
    optionType === ReturnOptionMethod.EXCHANGE &&
    option?.exchangeTypes?.advanced;

  return (
    <Flex className={returnCss.optionCard} dir="column" gap="md">
      <Flex align="center" dir="row" justify="space-between">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          {returnOptionMethod(optionType)}
        </Text>
        <RedoToggle
          disabled={!canEditReturnsSettings}
          setValue={handleToggle}
          size="xs"
          value
        />
      </Flex>
      <AdjustmentElement
        adjustmentFlat={option?.adjustmentFlat || "0"}
        adjustmentPercent={option?.adjustmentPercent || "0"}
        canEditReturnsSettings={canEditReturnsSettings}
        currencyOverrides={option?.currencyOverrides}
        handleAdjustmentFlat={handleAdjustmentFlat}
        handleAdjustmentPercent={handleAdjustmentPercent}
        handleCurrencyOverridesChange={handleCurrencyOverrides}
        handlePerCurrencyAmountsEnabledChange={handlePerCurrencyAmountsEnabled}
        perCurrencyAmountsEnabled={option?.perCurrencyAmountsEnabled}
      />
      {racEnabled && optionHasAdvancedExchanges && isBonus && (
        <Flex align="center" gap="xs">
          <RedoToggle
            disabled={!canEditReturnsSettings}
            label="Use it or lose it"
            setValue={handleIsUseItOrLoseIt}
            size="xs"
            value={option?.isUseItOrLoseIt ?? false}
          />
          <InfoTooltip
            boxSize={16}
            tooltip={{
              title:
                "If enabled, any excess bonus not applied to the exchange will be lost rather than rolling over as store credit or refund.",
            }}
          />
        </Flex>
      )}
      <RedoCollapseSubsection title="Advanced options">
        <RedoToggle
          description="Customer does not send item back."
          disabled={!canEditReturnsSettings}
          label="Green return"
          setValue={handleGreenReturn}
          size="xs"
          value={!option!.ship}
        />
        <RedoToggle
          description="Merchant pays for labels not covered by Redo."
          disabled={!canEditReturnsSettings}
          label="Safety net"
          setValue={handleSafetyNet}
          size="xs"
          value={option!.labelPayer === Payer.MERCHANT}
        />
        <RedoToggle
          description="Merchant will handle the shipping label"
          disabled={!canEditReturnsSettings}
          label="Manual Label"
          setValue={handleManualLabel}
          size="xs"
          value={option!.manual_label ?? false}
        />
        <RedoToggle
          description="Controls whether merchants are reimbursed for final sale returns processed through this option"
          disabled={!canEditReturnsSettings}
          label="Reimburse for final sale"
          setValue={handleReimburseForFinalSale}
          size="xs"
          value={option!.reimburseForFinalSale ?? true}
        />
        {optionType === ReturnOptionMethod.EXCHANGE && (
          <>
            <Flex dir="column" gap="sm">
              <Flex dir="column" gap="xxs">
                <Text fontSize="sm" fontWeight="medium" textColor="primary">
                  Allow exchange types
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  Select what types of exchanges to allow.
                </Text>
                {option!.exchangeTypes!.advanced &&
                  team.settings.exchanges.advancedExchanges ===
                    AdvancedExchanges.DISABLED && (
                    <Text fontSize="xs" textColor="secondary">
                      ⚠ Advanced exchanges are disabled globally in your
                      Exchanges settings.
                    </Text>
                  )}
              </Flex>
              <Flex dir="column" gap="xs">
                <RedoCheckbox
                  disabled={!canEditReturnsSettings}
                  label="Same item"
                  setValue={(v) => handleExchangeTypeSameItem(v === true)}
                  size="sm"
                  value={option!.exchangeTypes!.sameItem}
                />
                <RedoCheckbox
                  disabled={!canEditReturnsSettings}
                  label="Variant items"
                  setValue={(v) => handleExchangeTypeVariant(v === true)}
                  size="sm"
                  value={option!.exchangeTypes!.variant}
                />
                <RedoCheckbox
                  disabled={!canEditReturnsSettings}
                  label="Exchange groups"
                  setValue={(v) => handleExchangeTypeExchangeGroups(v === true)}
                  size="sm"
                  value={option!.exchangeTypes!.exchangeGroups}
                />
                <RedoCheckbox
                  disabled={!canEditReturnsSettings}
                  label="Any item"
                  setValue={(v) => handleExchangeTypeAdvanced(v === true)}
                  size="sm"
                  value={option!.exchangeTypes!.advanced}
                />
              </Flex>
            </Flex>
            <RedoToggle
              description="Offer the customer an add-on product if they do variant or same-item exchange. The customer will be charged the cost of the product."
              disabled={!canEditReturnsSettings}
              label="Variant exchange add-on"
              setValue={(value) => {
                const index = getIndex();
                if (index === -1) return;
                setState((state) =>
                  produce(state, (state) => {
                    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
                    state.options[index].variantExchangeAddOn = value;
                  }),
                );
              }}
              size="xs"
              value={option!.variantExchangeAddOn ?? false}
            />
            {option!.variantExchangeAddOn && (
              <Flex dir="column" gap="lg">
                <ProductAutocomplete
                  label="Product to add to exchange"
                  productsState={productsState}
                  setValue={handleVariantExchangeVariantId}
                  value={selectedProduct}
                />
                <RedoTextInput
                  label="Title text"
                  setValue={handleVariantExchangeAddOnTitle}
                  value={option?.variantExchangeAddOnTitle ?? ""}
                />
                <Flex dir="row" gap="md">
                  <RedoTextInput
                    fullWidth
                    label="Accept button text"
                    setValue={handleVariantExchangeAddOnAcceptButton}
                    value={option?.variantExchangeAddOnAcceptButton ?? ""}
                  />
                  <RedoTextInput
                    fullWidth
                    label="Reject button text"
                    setValue={handleVariantExchangeAddOnRejectButton}
                    value={option?.variantExchangeAddOnRejectButton ?? ""}
                  />
                </Flex>
              </Flex>
            )}
            <RedoToggle
              description="If enabled, customer will be charged for outgoing shipping of exchange orders."
              disabled={!canEditReturnsSettings}
              label="Charge customer for new order shipping"
              setValue={handleExchangeShipping}
              size="xs"
              value={option!.chargeCustomerForNewOrderShipping ?? false}
            />
          </>
        )}
      </RedoCollapseSubsection>
      {team.settings.marketing?.loyalty?.enabled && (
        <CollapseSubsection title="Loyalty options">
          <RedoToggle
            description="Refund points that were redeemed for this specific item"
            disabled={!canEditReturnsSettings}
            label="Refund points redeemed for this item"
            setValue={handleRefundPointsRedeemed}
            size="xs"
            value={option!.refundPointsRedeemedForItem ?? false}
          />
          <RedoToggle
            description="Deduct loyalty points earned from this purchase from the refund amount"
            disabled={!canEditReturnsSettings}
            label="Deduct earned points from refund"
            setValue={handleDeductEarnedPoints}
            size="xs"
            value={option!.deductEarnedPointsFromRefund ?? false}
          />
        </CollapseSubsection>
      )}
    </Flex>
  );
});
